import { getAgentBaseUrl } from "../ai-assistant/conversationClient";

export function normalizeSuperuserCargo(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "") === "superusuario";
}

export function canViewFeederScore(value) {
  const normalized = String(value || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");
  return ["jefearea", "jefedearea", "jeferegion", "jefederegion", "gerencia", "superusuario"].includes(normalized);
}

export function canViewAlarmReaction(value) {
  const normalized = String(value || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");
  return ["jeferegion", "jefederegion", "gerencia", "superusuario"].includes(normalized);
}

export function deriveCompanyId(value) {
  const raw = String(value || "").trim();
  if (!raw) return "default";
  try {
    const url = new URL(raw.includes("://") ? raw : `//${raw}`, window.location.origin);
    return (url.hostname || raw).replace(/[^a-zA-Z0-9_.-]/g, "-").replace(/^-+|-+$/g, "").toLowerCase() || "default";
  } catch {
    return raw.replace(/[^a-zA-Z0-9_.-]/g, "-").replace(/^-+|-+$/g, "").toLowerCase() || "default";
  }
}

function adminParams({ username, cargo }) {
  return {
    requestUsername: username || "",
    cargo: cargo || "",
  };
}

async function readJson(response, fallbackMessage) {
  const text = await response.text();
  let payload = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }
  if (!response.ok) {
    throw new Error(payload?.error || payload?.detail || text || fallbackMessage);
  }
  return payload;
}

export function resolveAgentBaseUrl(aiWebhookUrl) {
  return getAgentBaseUrl(aiWebhookUrl);
}

export async function fetchAiUsageSummary({
  agentBaseUrl,
  month,
  companyId,
  username,
  cargo,
  service,
}) {
  const params = new URLSearchParams({
    ...adminParams({ username, cargo }),
    month,
    companyId,
  });
  if (service) params.set("service", service);
  const response = await fetch(`${agentBaseUrl}/agent/v1/ai-usage/summary?${params}`);
  return readJson(response, "Error al cargar resumen de uso IA");
}

export async function fetchAiUsageEvents({
  agentBaseUrl,
  month,
  companyId,
  username,
  cargo,
  service,
  status,
  limit = 100,
}) {
  const params = new URLSearchParams({
    ...adminParams({ username, cargo }),
    month,
    companyId,
    limit: String(limit),
  });
  if (service) params.set("service", service);
  if (status) params.set("status", status);
  const response = await fetch(`${agentBaseUrl}/agent/v1/ai-usage/events?${params}`);
  return readJson(response, "Error al cargar eventos de uso IA");
}

export async function fetchAiUsageLimits({ agentBaseUrl, scope, username, cargo }) {
  const params = new URLSearchParams(adminParams({ username, cargo }));
  if (scope) params.set("scope", scope);
  const response = await fetch(`${agentBaseUrl}/agent/v1/ai-usage/limits?${params}`);
  return readJson(response, "Error al cargar limites de uso IA");
}

export async function upsertAiUsageLimit({ agentBaseUrl, payload, username, cargo }) {
  const response = await fetch(`${agentBaseUrl}/agent/v1/ai-usage/limits`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ...payload,
      requestUsername: username || "",
      cargo: cargo || "",
    }),
  });
  return readJson(response, "Error al guardar limite de uso IA");
}

export async function fetchAiAccessConfig({ agentBaseUrl, username, cargo }) {
  const params = new URLSearchParams(adminParams({ username, cargo }));
  const response = await fetch(`${agentBaseUrl}/agent/v1/ai-access/config?${params}`);
  return readJson(response, "Error al cargar acceso del asistente IA");
}

export async function fetchEffectiveAiAccess({ agentBaseUrl, username }) {
  const params = new URLSearchParams({ username: username || "" });
  const response = await fetch(`${agentBaseUrl}/agent/v1/ai-access/effective?${params}`, {
    cache: "no-store",
  });
  return readJson(response, "Error al cargar acceso efectivo del asistente IA");
}

export async function upsertGlobalAiAccess({ agentBaseUrl, access, username, cargo }) {
  const response = await fetch(`${agentBaseUrl}/agent/v1/ai-access/global`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      access,
      requestUsername: username || "",
      cargo: cargo || "",
    }),
  });
  return readJson(response, "Error al guardar acceso global del asistente IA");
}

export async function upsertUserAiAccess({
  agentBaseUrl,
  targetUsername,
  access,
  username,
  cargo,
}) {
  const response = await fetch(
    `${agentBaseUrl}/agent/v1/ai-access/users/${encodeURIComponent(targetUsername || "")}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        access,
        requestUsername: username || "",
        cargo: cargo || "",
      }),
    }
  );
  return readJson(response, "Error al guardar acceso de usuario del asistente IA");
}

export async function deleteUserAiAccess({ agentBaseUrl, targetUsername, username, cargo }) {
  const params = new URLSearchParams(adminParams({ username, cargo }));
  const response = await fetch(
    `${agentBaseUrl}/agent/v1/ai-access/users/${encodeURIComponent(targetUsername || "")}?${params}`,
    { method: "DELETE" }
  );
  return readJson(response, "Error al quitar override de acceso del asistente IA");
}
